import { useState, useEffect, useCallback } from 'react';

interface CrudPageHook<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  success: string | null;
  fetchItems: () => Promise<void>;
  createItem: (data: any) => Promise<void>;
  updateItem: (id: string, data: any) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  clearSuccess: () => void;
}

interface CrudPageOptions {
  listFn: () => Promise<any>;
  createFn?: (data: any) => Promise<any>;
  updateFn?: (id: string, data: any) => Promise<any>;
  deleteFn?: (id: string) => Promise<any>;
}

export function useCrudPage<T>(options: CrudPageOptions): CrudPageHook<T> {
  const { listFn, createFn, updateFn, deleteFn } = options;
  
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listFn();
      setItems(response.data || []);
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status === 404) {
        setItems([]);
        setError(null);
      } else {
        console.error('Error fetching items:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to fetch items');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  const createItem = async (data: any) => {
    if (!createFn) throw new Error('Create function not provided');
    
    try {
      setLoading(true);
      setError(null);
      await createFn(data);
      setSuccess('Item created successfully');
      await fetchItems();
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status !== 404) {
        console.error('Error creating item:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to create item');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (id: string, data: any) => {
    if (!updateFn) throw new Error('Update function not provided');
    
    try {
      setLoading(true);
      setError(null);
      await updateFn(id, data);
      setSuccess('Item updated successfully');
      await fetchItems();
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status !== 404) {
        console.error('Error updating item:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to update item');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (id: string) => {
    if (!deleteFn) throw new Error('Delete function not provided');
    
    try {
      setLoading(true);
      setError(null);
      await deleteFn(id);
      setSuccess('Item deleted successfully');
      await fetchItems();
    } catch (err: any) {
      // Silently handle 404 - endpoint not implemented yet
      if (err?.response?.status !== 404) {
        console.error('Error deleting item:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to delete item');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearSuccess = () => setSuccess(null);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return {
    items,
    loading,
    error,
    success,
    fetchItems,
    createItem,
    updateItem,
    deleteItem,
    clearSuccess,
  };
}
